import type { ApiClient } from "@planner/core";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";

/**
 * **«إيه الجديد» — نافذةٌ تُقرأ مرّةً واحدةً لكل نسخة.**
 *
 * **وأثرُها في صفّ صاحبها لا في متصفّحه** (قرار شادي): فمن يفتح من تليفونه
 * بعد حاسبه **لا يُقرأ عليه الخبرُ مرّتين**، ومن مسح متصفّحَه **لا يُقرأ عليه
 * تاريخُ النسخ كلُّه من جديد**. وتخزينُ المتصفّح كان سيفعل الاثنين.
 *
 * **ولكل من دخل**، لا لمن يملك سجلَّ التغييرات: **فالخبرُ عن النظام الذي
 * يستعملونه**. ولو حُرِست بقدرة الكتابة **لما رأى الجديدَ إلّا من كتبه**.
 *
 * **وفشلُ قراءتها لا يُرفَع توستاً**: فهي ليست فعلاً طلبه أحد، **وتوستُ خطأٍ
 * في كل دخولٍ على شبكةٍ متقطّعة ضجيجٌ يُدرِّب الناسَ على تجاهل التوست** — وهو
 * أخطرُ من ألّا يُقرأ خبرُ نسخةٍ اليوم. **والنافذةُ تُحاول في كل دخول**، فلا
 * يضيع الخبر: ما لم يُوسَم مقروءاً يبقى معلَّقاً.
 */

type Release = {
  readonly id: string;
  readonly version: string;
  readonly titleAr: string;
  readonly titleEn: string;
  readonly bodyAr: string;
  readonly bodyEn: string;
};

export function WhatsNew({ client }: { readonly client: ApiClient }): ReactNode {
  const { t, locale } = useLocale();
  const [release, setRelease] = useState<Release | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      const result = await client.request<Release | null>("/platform/whats-new", {
        signal: controller.signal,
      });
      if (controller.signal.aborted || !result.ok) return;
      setRelease(result.data);
    })();
    return () => {
      controller.abort();
    };
  }, [client]);

  const seen = useCallback(async (): Promise<void> => {
    if (release === null || busy) return;
    setBusy(true);
    /**
     * **وتُغلَق أوّلاً ثم يُكتَب الإقرار.**
     *
     * **فنافذةٌ تبقى حتى يرجع السيرفر تُحبِس من ضغط الزرَّ** على شبكةٍ بطيئة —
     * والإقرارُ لا يُغيّر شيئاً يراه: أقصى أثرِ فشلِه أن تُفتَح مرّةً ثانية.
     */
    setRelease(null);
    await client.request(`/platform/whats-new/${release.id}/seen`, {
      method: "POST",
      idempotencyKey: platform.newId(),
      body: {},
    });
    setBusy(false);
  }, [busy, client, release]);

  if (release === null) return null;

  return (
    <div
      className="mod"
      role="dialog"
      aria-modal="true"
      aria-label={t("platform.changelog.whatsNew")}
    >
      <div className="mod-head">
        <b>{t("platform.changelog.whatsNew")}</b>
      </div>
      <div className="confirm-body">
        <p className="confirm-what">{locale === "ar" ? release.titleAr : release.titleEn}</p>
        <p className="field-hint">
          <span className="tm">{t("platform.changelog.version")}</span>
          {release.version}
        </p>
        <p>{locale === "ar" ? release.bodyAr : release.bodyEn}</p>
      </div>
      <div className="mod-actions">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            void seen();
          }}
        >
          {t("platform.changelog.gotIt")}
        </button>
      </div>
    </div>
  );
}
